import React from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { List } from 'immutable';

import { color } from '../../../styles/helpers/variables';
import { SITE_ROOT_URL } from '../../../constants/endpoints';
import { toCurrency } from '../../../util/toCurrency';

import { getProductVariantName } from '../../../util/getProductVariantName';

class ProductPageSelect extends React.Component {
  constructor(props) {
    super(props);
    this.selectContainerRef = React.createRef();
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.toggleActive = this.toggleActive.bind(this);
    this.state = {
      active: false
    };
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleMouseDown);
    document.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleMouseDown);
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  handleMouseDown(event) {
    const element = this.selectContainerRef.current;
    if ((!element || !element.contains(event.target)) && this.state.active) {
      this.setState({ active: false });
    }
  }

  handleKeyDown(event) {
    if (event.keyCode === 27 && this.state.active) {
      this.setState({ active: false });
    }
  }

  toggleActive() {
    this.setState({ active: !this.state.active });
  }

  render() {
    const {
      label,
      image,
      title,
      type,
      options,
      setProductVariant,
      selected,
      location,
      price,
      selectedOptionPrice,
      totalSelectedOptionPrice
    } = this.props;
    const { active } = this.state;

    const formattedTitle = getProductVariantName(title);

    return (
      <Select>
        <SelectLabel>{label || 'Utförande'}</SelectLabel>
        <SelectPlaceholderContainer>
          <SelectContainer active={active} innerRef={this.selectContainerRef}>
            <SelectButton active={active} onClick={this.toggleActive}>
              <SelectButtonImage>
                <img src={image} alt={formattedTitle} />
              </SelectButtonImage>
              <SelectButtonText>{formattedTitle}</SelectButtonText>
            </SelectButton>
            <SelectOptionList active={active}>
              {options.map(option => {
                const isSelected = type ? selected === option.get('id') : location === option.get('url');
                if (isSelected) {
                  return null;
                }
                let relativePrice = null;
                if (type === 'variant') {
                  relativePrice =
                    option.get('price') - price + totalSelectedOptionPrice !== 0
                      ? option.get('price') - price + totalSelectedOptionPrice
                      : null;
                } else {
                  relativePrice =
                    option.get('price') - selectedOptionPrice !== 0 ? option.get('price') - selectedOptionPrice : null;
                }
                let relativePricePrefix = null;
                if (relativePrice && relativePrice < 0) {
                  relativePricePrefix = '-';
                } else if (relativePrice && relativePrice > 0) {
                  relativePricePrefix = '+';
                }
                return (
                  <SelectOptionListItem key={`select-option-list-item-${option.get('name')}`}>
                    <SelectOptionButton
                      type="button"
                      onClick={() => {
                        this.toggleActive();
                        setProductVariant(option.get('id'), type);
                      }}
                    >
                      <SelectOptionImage>
                        <img
                          src={
                            SITE_ROOT_URL + (option.getIn(['mainImage', 'url']) || option.getIn(['images', 0, 'url']))
                          }
                          alt={option.get('name')}
                        />
                      </SelectOptionImage>
                      <SelectOptionName>
                        {getProductVariantName(option.get('cleanVariantName') || option.get('name'))}
                      </SelectOptionName>
                      {option.get('inStock') && (
                        <SelectOptionInStock>
                          <SelectOptionInStockText>I lager</SelectOptionInStockText>
                        </SelectOptionInStock>
                      )}
                      {relativePrice && (
                        <SelectOptionPrice>
                          {relativePricePrefix && `${relativePricePrefix} `}
                          {toCurrency(Math.abs(relativePrice))}
                        </SelectOptionPrice>
                      )}
                    </SelectOptionButton>
                  </SelectOptionListItem>
                );
              })}
            </SelectOptionList>
          </SelectContainer>
        </SelectPlaceholderContainer>
      </Select>
    );
  }
}

ProductPageSelect.defaultProps = {
  label: '',
  image: '',
  title: '',
  type: '',
  options: List(),
  setProductVariant: () => {},
  selected: '',
  location: '',
  price: null,
  selectedOptionPrice: null,
  totalSelectedOptionPrice: null
};

ProductPageSelect.propTypes = {
  label: PropTypes.string,
  image: PropTypes.string,
  title: PropTypes.string,
  type: PropTypes.string,
  options: PropTypes.instanceOf(List),
  setProductVariant: PropTypes.func,
  selected: PropTypes.string,
  location: PropTypes.string,
  price: PropTypes.number,
  selectedOptionPrice: PropTypes.number,
  totalSelectedOptionPrice: PropTypes.number
};

export default ProductPageSelect;

const Select = styled.div`
  margin: 0 0 10px;
  &:last-child {
    margin: 0;
  }
`;

const SelectLabel = styled.div`
  margin: 0 0 5px;
  color: #000000;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
`;

const SelectPlaceholderContainer = styled.div`
  position: relative;
  height: 56px;
`;
const SelectContainer = styled.div`
  position: absolute;
  width: 100%;
  z-index: 1;
  ${props =>
    props.active &&
    css`
      box-shadow: 0 0 10px rgb(0 0 0 / 0.1);
      z-index: 2;
    `};
`;

const SelectButton = styled.button`
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
  margin: 0;
  padding: 8px 45px 8px 8px;
  border: none;
  outline: none; // …
  background-color: #ffffff;
  cursor: pointer;
  &:after {
    content: '';
    position: absolute;
    top: 50%;
    right: 15px;
    height: 15px;
    width: 15px;
    background-image: url('/images/icons/arrow.svg');
    background-position: center;
    background-repeat: no-repeat;
    background-size: contain;
    transform: translateY(-50%);
    ${props =>
      props.active &&
      css`
        transform: translateY(-50%) rotate(180deg);
      `};
  }
`;
const SelectButtonImage = styled.div`
  flex: 0 0 auto;
  position: relative;
  height: 40px;
  width: 40px;
  margin: 0 15px 0 0;
  > img {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    object-fit: contain;
  }
`;
const SelectButtonText = styled.div`
  color: ${color.black};
  font-family: 'Open Sans';
  font-size: 16px;
  line-height: 1.25;
  font-weight: 400;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const SelectOptionList = styled.ul`
  display: none;
  list-style: none;
  max-height: 225px;
  margin: 0;
  padding: 0;
  border-top: 1px solid #f2f2f2;
  background-color: ${color.white};
  overflow-y: auto;
  ${props =>
    props.active &&
    css`
      display: block;
    `};
`;
const SelectOptionListItem = styled.li`
  border-bottom: 1px solid #f2f2f2;
  &:first-child {
    > button {
      padding-top: 8px;
    }
  }
  &:last-child {
    border-bottom: none;
    > button {
      padding-bottom: 8px;
    }
  }
`;
const SelectOptionButton = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 4px 15px 4px 8px;
  border: none;
  outline: none; // …
  background-color: transparent;
  color: inherit;
  font-size: inherit;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
`;
const SelectOptionImage = styled.div`
  flex: 0 0 auto;
  position: relative;
  height: 40px;
  width: 40px;
  margin: 0 15px 0 0;
  color: transparent;
  overflow: hidden;
  > img {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    object-fit: contain;
  }
`;
const SelectOptionName = styled.div`
  flex: 1 1 auto;
  margin: 0 15px 0 0;
  color: #000000;
  font-family: 'Open Sans';
  font-size: 16px;
  line-height: 1.25;
  font-weight: 400;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  &:last-child {
    margin: 0;
  }
`;
const SelectOptionInStock = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  margin: 0 15px 0 0;
  color: #6a6a6a;
  font-size: 0.875em;
  &:last-child {
    margin: 0;
  }
  &:before {
    content: '';
    flex: 0 0 auto;
    height: 5px;
    width: 5px;
    border-radius: 50%;
    background-color: ${color.success};
    @media screen and (min-width: 1150px) {
      margin: 0 5px 0 0;
    }
  }
`;
const SelectOptionInStockText = styled.span`
  display: none;
  @media screen and (min-width: 1150px) {
    display: inline;
  }
`;
const SelectOptionPrice = styled.div`
  flex: 0 0 auto;
  color: #6a6a6a;
  font-style: italic;
`;

export const Paragraph = styled.p`
  margin: 0 0 5px;
  font-size: 14px;
  font-weight: normal;
  line-height: 1.57;
  text-align: left;
  color: ${color.black};
  overflow: hidden;
  ${props =>
    props.active &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
    `};
`;

export const Button = styled.button`
  padding: 0;
  border: none;
  font-size: 14px;
  font-weight: normal;
  line-height: 1.57;
  text-align: left;
  color: ${color.black};
  text-decoration: underline;
  cursor: pointer;
`;



// WEBPACK FOOTER //
// ./src/components/productPage/ProductPageSelect/ProductPageSelect.js